import React, {useState, useEffect, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {
    DndContext,
    closestCenter,
    DragOverlay,
    useSensor,
    useSensors,
    PointerSensor,
    KeyboardSensor,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    Box,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    Typography,
    Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import {arrayMove} from '@dnd-kit/sortable';
import PropTypes from 'prop-types';

import {Section} from './Section.jsx';
import {Question} from './Question.jsx';

// Constants for question types
const QUESTION_TYPES = {
    TEXT: 'text',
    STRING: 'string',
    NUMBER: 'number',
    DATE: 'date',
    SINGLE_CHOICE: 'single_choice',
    MULTI_CHOICE: 'multi_choice',
};

// Status types for the save operation
const SAVE_STATUS = {
    IDLE: 'idle',
    SAVING: 'saving',
    SUCCESS: 'success',
    ERROR: 'error'
};

export const FormBuilder = ({initialData, onSave, editable = false, canToggleEdit = true}) => {
    // State initialization
    const [form, setForm] = useState(() => normalizeFormData(initialData));
    const [activeId, setActiveId] = useState(null);
    const [sectionDragging, setSectionDragging] = useState(false);
    const [isEditing, setIsEditing] = useState(editable);

    // New state for save status and snackbar
    const [saveStatus, setSaveStatus] = useState(SAVE_STATUS.IDLE);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    // Initialize sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
        useSensor(KeyboardSensor)
    );

    // Update isEditing when editable prop changes
    useEffect(() => {
        setIsEditing(editable);
    }, [editable]);

    // Handler for Finish button (similar to toggling edit mode off)
    const handleFinish = () => {
        if (canToggleEdit) {
            setIsEditing(false);
        }
    };

    // Handler for Cancel button (revert to initial data and exit edit mode)
    const handleCancel = () => {
        if (canToggleEdit) {
            // Reset form data to initial state
            setForm(normalizeFormData(initialData));
            // Exit edit mode
            setIsEditing(false);
        }
    };

    // Function to handle saving the form
    const handleSaveForm = useCallback(async () => {
        try {
            // Set status to saving
            setSaveStatus(SAVE_STATUS.SAVING);
            setSnackbarMessage('Saving form...');
            setSnackbarOpen(true);

            // Call the save function (assumed to be async)
            const savedForm = await onSave(form);

            // Always assume success - we just want to show feedback to the user
            // If savedForm exists and has a structure we can normalize, update the form
            if (savedForm) {
                try {
                    // Try to normalize and update, but don't make it a requirement for success
                    const normalizedForm = normalizeFormData(savedForm);
                    if (normalizedForm && normalizedForm.sections) {
                        setForm(normalizedForm);
                    }
                } catch (normalizationError) {
                    console.warn('Could not normalize returned form data:', normalizationError);
                    // This is just a warning, not an error - we still treat the save as successful
                }
            }

            // Always set success status unless there's a thrown exception
            setSaveStatus(SAVE_STATUS.SUCCESS);
            setSnackbarMessage('Form saved successfully!');

            // Close edit mode after successful save
            if (canToggleEdit) {
                setIsEditing(false);
            }

        } catch (error) {
            console.error('Error saving form:', error);
            setSaveStatus(SAVE_STATUS.ERROR);
            // Display the backend error message if available
            const errorMessage = error.response?.data?.detail ||
                                error.response?.data?.message ||
                                error.message ||
                                'Failed to save form. Please try again.';
            setSnackbarMessage(errorMessage);
            // Keep the user in edit mode so they can fix the issue and retry
        } finally {
            setSnackbarOpen(true);
        }
    }, [form, onSave, canToggleEdit]);

    // Handle snackbar close
    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    // SECTION MANAGEMENT
    const addSection = useCallback(() => {
        if (!isEditing) return;

        const newSection = createNewSection(form.sections.length);
        setForm(prev => ({
            ...prev,
            sections: [...prev.sections, newSection],
        }));
    }, [form.sections.length, isEditing]);

    const updateSection = useCallback((sectionId, updates) => {
        if (!isEditing) return;

        setForm(prev => ({
            ...prev,
            sections: prev.sections.map(section =>
                section.id === sectionId ? {...section, ...updates} : section
            ),
        }));
    }, [isEditing]);

    const deleteSection = useCallback((sectionId) => {
        if (!isEditing) return;

        setForm(prev => ({
            ...prev,
            sections: prev.sections
                .filter(section => section.id !== sectionId)
                .map((section, index) => ({...section, order: index})),
        }));
    }, [isEditing]);

    const addQuestion = useCallback((sectionId, type) => {
        if (!isEditing) return;

        const newQuestion = createNewQuestion(type);
        setForm(prev => {
            const sectionIndex = prev.sections.findIndex(s => s.id === sectionId);
            if (sectionIndex < 0) return prev;

            const newSections = [...prev.sections];
            newSections[sectionIndex] = {
                ...newSections[sectionIndex],
                questions: [
                    ...newSections[sectionIndex].questions,
                    {...newQuestion, order: newSections[sectionIndex].questions.length}
                ]
            };

            return {...prev, sections: newSections};
        });
    }, [isEditing]);

    const updateQuestion = useCallback((sectionId, questionId, updates) => {
        if (!isEditing) return;

        setForm(prev => ({
            ...prev,
            sections: prev.sections.map(section => {
                if (section.id !== sectionId) return section;
                return {
                    ...section,
                    questions: section.questions.map(question =>
                        question.id === questionId ? {...question, ...updates} : question
                    ),
                };
            }),
        }));
    }, [isEditing]);

    const deleteQuestion = useCallback((sectionId, questionId) => {
        if (!isEditing) return;

        setForm(prev => ({
            ...prev,
            sections: prev.sections.map(section => {
                if (section.id !== sectionId) return section;
                return {
                    ...section,
                    questions: section.questions
                        .filter(q => q.id !== questionId)
                        .map((q, idx) => ({...q, order: idx})),
                };
            }),
        }));
    }, [isEditing]);

    const moveQuestionToSection = useCallback((sourceSectionId, questionId, targetSectionId) => {
        if (!isEditing) return;
        if (sourceSectionId === targetSectionId) return;

        setForm(prev => {
            const sourceIndex = prev.sections.findIndex(s => s.id === sourceSectionId);
            const targetIndex = prev.sections.findIndex(s => s.id === targetSectionId);

            if (sourceIndex < 0 || targetIndex < 0) return prev;

            const newSections = [...prev.sections];

            // Find the question to move
            const questionIndex = newSections[sourceIndex].questions.findIndex(q => q.id === questionId);
            if (questionIndex < 0) return prev;

            const movedQuestion = {...newSections[sourceIndex].questions[questionIndex]};

            // Remove from source section
            newSections[sourceIndex] = {
                ...newSections[sourceIndex],
                questions: newSections[sourceIndex].questions
                    .filter(q => q.id !== questionId)
                    .map((q, idx) => ({...q, order: idx}))
            };

            // Add to target section
            newSections[targetIndex] = {
                ...newSections[targetIndex],
                questions: [
                    ...newSections[targetIndex].questions,
                    {...movedQuestion, order: newSections[targetIndex].questions.length}
                ]
            };

            return {...prev, sections: newSections};
        });
    }, [isEditing]);

    // DRAG HANDLERS (for reordering only)
    const handleItemMove = useCallback((active, over) => {
        if (!isEditing) return;

        // Section reordering
        if (active.id.startsWith('section-') && over.id.startsWith('section-')) {
            const oldIndex = form.sections.findIndex(s => `section-${s.id}` === active.id);
            const newIndex = form.sections.findIndex(s => `section-${s.id}` === over.id);

            setForm(prev => ({
                ...prev,
                sections: arrayMove(prev.sections, oldIndex, newIndex)
                    .map((section, idx) => ({...section, order: idx}))
            }));
            return;
        }

        // Question reordering within section
        if (active.id.startsWith('question-') && over.id.startsWith('question-')) {
            const sectionId = active.data.current.sectionId;
            const sectionIndex = form.sections.findIndex(s => s.id === sectionId);
            if (sectionIndex < 0) return;

            setForm(prev => {
                const newSections = [...prev.sections];
                const questions = newSections[sectionIndex].questions;
                const oldIndex = questions.findIndex(q => `question-${q.id}` === active.id);
                const newIndex = questions.findIndex(q => `question-${q.id}` === over.id);

                newSections[sectionIndex] = {
                    ...newSections[sectionIndex],
                    questions: arrayMove(questions, oldIndex, newIndex)
                        .map((q, idx) => ({...q, order: idx}))
                };

                return {...prev, sections: newSections};
            });
            return;
        }

        // Moving question between sections
        if (active.id.startsWith('question-') && over.id.startsWith('section-')) {
            const sourceSectionId = active.data.current.sectionId;
            const targetSectionId = over.id.replace('section-', '');
            if (sourceSectionId === targetSectionId) return;

            setForm(prev => {
                const sourceIndex = prev.sections.findIndex(s => s.id === sourceSectionId);
                const targetIndex = prev.sections.findIndex(s => s.id === targetSectionId);
                if (sourceIndex < 0 || targetIndex < 0) return prev;

                const newSections = [...prev.sections];

                // Find the question that needs to be moved
                const questionId = active.id.replace('question-', '');
                const questionIndex = newSections[sourceIndex].questions.findIndex(q => q.id === questionId);

                if (questionIndex < 0) return prev;

                // Extract only that specific question
                const movedQuestion = {...newSections[sourceIndex].questions[questionIndex]};

                // Remove the question from the source section
                newSections[sourceIndex].questions = newSections[sourceIndex].questions
                    .filter(q => q.id !== questionId)
                    .map((q, i) => ({...q, order: i}));

                // Add the question to the target section
                newSections[targetIndex].questions.push({
                    ...movedQuestion,
                    order: newSections[targetIndex].questions.length
                });

                return {...prev, sections: newSections};
            });
        }
    }, [form, isEditing]);

    const handleDragStart = useCallback(({active}) => {
        if (!isEditing) return;

        setActiveId(active.id);
        // Set sectionDragging to true if a section is being dragged
        if (active.id.startsWith('section-')) {
            setSectionDragging(true);
        }
    }, [isEditing]);

    const handleDragEnd = useCallback(({active, over}) => {
        if (!isEditing) return;

        if (!over) {
            setActiveId(null);
            setSectionDragging(false); // Reset section dragging state
            return;
        }

        // Handle other drag operations
        if (active.id !== over.id) {
            handleItemMove(active, over);
        }

        setActiveId(null);
        setSectionDragging(false); // Reset section dragging state
    }, [handleItemMove, isEditing]);

    const handleDragCancel = useCallback(() => {
        setActiveId(null);
        setSectionDragging(false); // Reset section dragging state
    }, []);

    // RENDER LOGIC
    const getActiveItem = useCallback(() => {
        if (!activeId || !isEditing) return null;

        if (activeId.startsWith('section-')) {
            const section = form.sections.find(s => `section-${s.id}` === activeId);
            return section ? <Section id={activeId} section={section} isOverlay isAnySectionDragging={sectionDragging}
                                      editable={isEditing}/> : null;
        }

        if (activeId.startsWith('question-')) {
            for (const section of form.sections) {
                const question = section.questions.find(q => `question-${q.id}` === activeId);
                if (question) {
                    return (
                        <Question
                            id={activeId}
                            question={question}
                            isOverlay
                            editable={isEditing}
                            availableSections={form.sections}
                            onMoveToSection={moveQuestionToSection}
                        />
                    );
                }
            }
        }

        return null;
    }, [activeId, form.sections, isEditing, sectionDragging, moveQuestionToSection]);

    return (
        <Box sx={{display: 'flex', p: 2}}>
            <Box sx={{width: '100%'}}>
                {/* Header with Edit/Finish/Cancel buttons on the left and Save button on the right */}
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                    {/* Left side buttons: Edit (when not editing) or Finish/Cancel (when editing) */}
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        {!isEditing && canToggleEdit ? (
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setIsEditing(true)}
                                startIcon={<EditIcon/>}
                            >
                                Edit
                            </Button>
                        ) : isEditing && canToggleEdit && (
                            <>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleFinish}
                                    startIcon={<CloseIcon/>}
                                >
                                    Close
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={handleCancel}
                                    startIcon={<CancelIcon/>}
                                >
                                    Cancel
                                </Button>
                            </>
                        )}
                    </Box>

                    {/* Right side: Save Form Button - only visible in edit mode */}
                    {isEditing && (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleSaveForm}
                            disabled={saveStatus === SAVE_STATUS.SAVING}
                            startIcon={saveStatus === SAVE_STATUS.SAVING ?
                                <CircularProgress size={20} color="inherit"/> :
                                <SaveIcon/>}
                        >
                            {saveStatus === SAVE_STATUS.SAVING ? 'Saving...' : 'Save'}
                        </Button>
                    )}
                </Box>

                {/* Form content */}
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    <Box>
                        {/* Form Sections */}
                        <SortableContext
                            items={form.sections.map(s => `section-${s.id}`)}
                            strategy={verticalListSortingStrategy}
                        >
                            {form.sections.map(section => (
                                <Section
                                    key={section.id}
                                    id={`section-${section.id}`}
                                    section={section}
                                    onUpdate={updateSection}
                                    onDelete={deleteSection}
                                    onAddQuestion={addQuestion}
                                    isAnySectionDragging={sectionDragging}
                                    editable={isEditing}
                                >
                                    <SortableContext
                                        items={section.questions.map(q => `question-${q.id}`)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {section.questions.map(question => (
                                            <Question
                                                key={question.id}
                                                id={`question-${question.id}`}
                                                sectionId={section.id}
                                                question={question}
                                                onUpdate={updateQuestion}
                                                onDelete={deleteQuestion}
                                                editable={isEditing}
                                                availableSections={form.sections}
                                                onMoveToSection={moveQuestionToSection}
                                            />
                                        ))}
                                    </SortableContext>
                                </Section>
                            ))}
                        </SortableContext>
                    </Box>

                    {createPortal(
                        <DragOverlay>
                            {getActiveItem()}
                        </DragOverlay>,
                        document.body
                    )}
                </DndContext>

                {/* Add Section button - only visible in edit mode */}
                {isEditing && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon/>}
                        onClick={addSection}
                        sx={{mt: 3}}
                    >
                        Add Section
                    </Button>
                )}

                {/* Status Snackbar */}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={
                        saveStatus === SAVE_STATUS.SAVING ? null :
                        saveStatus === SAVE_STATUS.ERROR ? null :
                        4000
                    }
                    onClose={handleSnackbarClose}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                >
                    <Alert
                        onClose={handleSnackbarClose}
                        severity={
                            saveStatus === SAVE_STATUS.SUCCESS ? 'success' :
                                saveStatus === SAVE_STATUS.ERROR ? 'error' :
                                    'info'
                        }
                        sx={{width: '100%'}}
                        variant={saveStatus === SAVE_STATUS.ERROR ? 'filled' : 'standard'}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

// Helper functions outside the component
function normalizeFormData(initialData) {
    if (!initialData || initialData.length === 0) {
        return {
            application_period_id: '',
            id: '',
            sections: []
        };
    }

    const data = Array.isArray(initialData) ? initialData[0] : initialData;

    return {
        ...data,
        sections: (data.sections || []).map((section, index) => ({
            ...section,
            id: section.id || generateId('section'),
            order: section.order ?? index,
            questions: (section.questions || []).map((question, qIndex) => ({
                ...question,
                id: question.id || generateId('question'),
                order: question.order ?? qIndex,
                options: (question.options || []).map((option, oIndex) => ({
                    ...option,
                    id: option.id || generateId('option'),
                    order: option.order ?? oIndex
                }))
            }))
        }))
    };
}

function createNewSection(order) {
    return {
        id: generateId('section'),
        title: 'New Section',
        description: '',
        order,
        questions: []
    };
}

function createNewQuestion(type) {
    const baseQuestion = {
        id: generateId('question'),
        text: 'New Question',
        type,
        required: false,
        hidden: false,
        disabled: false,
        config: {},
        order: 0
    };

    if (type === QUESTION_TYPES.SINGLE_CHOICE || type === QUESTION_TYPES.MULTI_CHOICE) {
        return {
            ...baseQuestion,
            options: [
                {
                    id: generateId('option'),
                    label: 'Option 1',
                    value: 'option1',
                    order: 0
                }
            ]
        };
    }

    return baseQuestion;
}

function generateId(prefix) {
    return `${prefix}-${crypto.randomUUID()}`;
}

// PropTypes validation
FormBuilder.propTypes = {
    initialData: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.shape({
            application_period_id: PropTypes.string,
            id: PropTypes.string,
            sections: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string,
                    title: PropTypes.string,
                    description: PropTypes.string,
                    order: PropTypes.number,
                    questions: PropTypes.arrayOf(
                        PropTypes.shape({
                            id: PropTypes.string,
                            text: PropTypes.string,
                            type: PropTypes.oneOf(Object.values(QUESTION_TYPES)),
                            required: PropTypes.bool,
                            hidden: PropTypes.bool,
                            disabled: PropTypes.bool,
                            config: PropTypes.object,
                            order: PropTypes.number,
                            options: PropTypes.arrayOf(
                                PropTypes.shape({
                                    id: PropTypes.string,
                                    label: PropTypes.string,
                                    value: PropTypes.string,
                                    order: PropTypes.number
                                })
                            )
                        })
                    )
                })
            )
        })
    ]),
    onSave: PropTypes.func.isRequired,
    editable: PropTypes.bool,
    canToggleEdit: PropTypes.bool
};